import { useCallback, useContext, useMemo } from 'react'
import { UserContext, UserContextType } from '../UserContext'
import { RoomContext, RoomContextType } from '../RoomContext'
import { getPinInfo, useViewMode } from '@/pages/SessionPage/internal/shared/useViewMode'
import { persistent } from './LocalStorage'

export default function useRoomViews() {
    const { state } = useContext(UserContext) as UserContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType

    const viewMode = useViewMode()
    const pinInfo = getPinInfo(viewMode)
    const presentingPinnedPeerSessionId = pinInfo?.kind === 'screen' ? pinInfo?.peerSessionId || null : null

    const isPresenting =
        state.mediaSelection.desktop !== '_none' &&
        state.mediaSelection.desktop !== '_undef' &&
        state.mediaStreams.desktop instanceof MediaStream

    const showWaitingRoomPopup = (() => {
        const isSubroom = !!roomState.parentRoomUrn
        if (roomState.isSidebarRetracted) {
            return false
        }
        if (roomState.isForceShowingWaitingRoomPopup) {
            return true
        }
        if (isSubroom) {
            return false
        }
        return false
        // return roomState.isHost && Object.keys(roomState.peers).length === 0 && roomState.isShowingWaitingRoomPopup
    })()

    const collaboratorCount = useMemo(() => {
        // + 1 for including myself.
        return Object.keys(roomState.peers).length + 1
    }, [roomState.peers])

    // Indicates if the main video gallery is in a state where it cannot show
    // all peer video streams. When this is true, many video streams should be
    // pushed into the sidebar instead.
    const isVideoGalleryBusy = useMemo(() => {
        return (
            roomState.isRetracted ||
            roomState.isShowingGalleryQuickSetupGuide ||
            presentingPinnedPeerSessionId ||
            roomState.pinnedPeerId !== null
        )
    }, [
        roomState.isRetracted,
        roomState.isShowingGalleryQuickSetupGuide,
        presentingPinnedPeerSessionId,
        roomState.pinnedPeerId,
    ])

    const canTogglePermanentRoom = useMemo(() => {
        if (!roomState.isHost) {
            return false
        }

        if (roomState.parentRoomUrn) {
            return false
        }

        const maxPermanentRoomCount = state.profile?.policy?.maxPermanentRoomCount || 0
        if (!maxPermanentRoomCount || maxPermanentRoomCount < 0) {
            return false
        }

        return true
    }, [roomState.isHost, state.profile])

    const isMaxPermanentRoomCount = useMemo(() => {
        const permanentRooms = persistent.getArray<any>('permanentRooms') || []
        const maxPermanentRoomCount = state.profile?.policy?.maxPermanentRoomCount || 0

        if (permanentRooms.length >= maxPermanentRoomCount) {
            return true
        } else {
            return false
        }
    }, [roomState.isHost, state.profile])

    return {
        isPresenting,
        showWaitingRoomPopup,
        collaboratorCount,
        isVideoGalleryBusy,
        canTogglePermanentRoom,
        isMaxPermanentRoomCount,
        presentingPinnedPeerSessionId,
    }
}
